import { usePortalMessage } from "../../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * The Agent header's lifecycle: what state the agent is really in, what the owner can do about it,
 * and — when something is wrong — why (customer-portal review 2026-09-25).
 *
 *   #106  "Pause agent" did nothing. Now it pauses, with a confirmation that says what callers
 *         hear, and "Resume" undoes it. Not offered on an agent that never went live.
 *   #105  There was no way to remove an agent. "Remove agent" archives one that is not live (a
 *         paused one too, once no number reaches it); it leaves the list and every picker.
 *   #32 / #33 / #110 / #130  A failed agent's page never said why, and offered nothing. It now leads
 *         with the failure in the owner's words, and "Try again" (same documents) / "Replace
 *         document" (new ones) / "Remove agent".
 *   #120 / #131 / #74  A live agent nothing reaches read "Live" and "answering". It now reads
 *         "Live, not reachable", and its next step is "Connect a number".
 *
 * Every refusal shows the API's own sentence, which already says what to do ("Pause it first",
 * "ask WeTalk to move the number").
 *
 * AMENDED 2026-10-19 (plan-021 WAPP-PASS, design §33.13.2, ADR 0074 proposed): the pause and remove confirmations name
 * the agents that pass conversations to this one (`GET …/pass-referrers`, read while the confirmation is open) — "These
 * agents pass conversations to {name}: … Afterwards they keep those conversations themselves." — and say nothing when
 * none does, or when the list cannot be read (the confirmation never waits on it).
 *
 * AMENDED 2026-10-28 (owner request: "add rename"): "Rename" opens a small dialog with the name box, pre-filled with
 * the name it has. Saving sends `PUT /v1/agents/{id}/name` — the name only, no version and no build — and every read
 * that names agents (the list, History, Live, campaigns, the numbers, the pass panels, the dashboard) is read again, so
 * the new name shows everywhere at once. A refusal stays in the dialog, in the API's own words.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { VoiceAgentAudienceTag } from "../agent-audience.js";
import { Button, Dialog, Field, Input, type TagStatus } from "@wetalk/ui";
import { useRef, useState, type FormEvent, type ReactNode } from "react";

import { apiProblemText } from "../../../api/api-error.js";
import { useGateway } from "../../../api/provider.js";
import { QUERY_KEY } from "../../../api/query.js";
import type { VoiceAgentView } from "../../../api/gateway.js";
import { lifecycleRefusalOf } from "../../../api/slice/voice-agent-lifecycle.js";
import { VOICE_AGENT_PASS_QUERY_KEY, useVoiceAgentPassReferrers } from "../../../api/slice/voice-agent-pass.js";
import { referrersTextOf } from "./channels/voice-agent-pass-copy.js";
import { rememberBuildOrigin } from "../../building/build-origin.js";
import { useMemberCan } from "../../../shell/member-capability.js";

import "./lifecycle.css";

/** The longest name, as the API and the wizard's name box allow it. */
const VOICE_AGENT_NAME_MOST = 120;

/** The file types the factory reads — the same list the wizard's upload accepts. */
const DOCUMENT_ACCEPT = ".pdf,.jpg,.jpeg,.png,.txt,.md,.markdown,.csv";

/** The words on the header's tag, and its tone. "Live" only when something can reach it (#120). */
export function voiceAgentStatusOf(one: VoiceAgentView): { readonly label: string; readonly tone: TagStatus } {
  if (one.pausedBy === "operator") return { label: portalText("operations", "paused_by_wetalk_fa955849"), tone: "critical" };
  switch (one.state) {
    case "live":
      return one.reachable ? { label: portalText("operations", "live_65c821a5"), tone: "positive" } : { label: portalText("operations", "live_not_reachable_8939344a"), tone: "caution" };
    case "paused":
      return { label: portalText("operations", "paused_c7dfb6f1"), tone: "muted" };
    case "failed":
      return { label: portalText("operations", "build_failed_2f8e461f"), tone: "critical" };
    case "building":
      return one.latestBuild !== null && one.latestBuild.waitingForDocument
        ? { label: portalText("operations", "waiting_for_documents_3cfbfc88"), tone: "caution" }
        : { label: portalText("operations", "building_9d914bfc"), tone: "caution" };
    case "scheduled":
      return { label: portalText("operations", "scheduled_1cd1bdad"), tone: "caution" };
    case "draft":
      return { label: portalText("operations", "draft_23d33e22"), tone: "muted" };
  }
}

type Pending = "pause" | "resume" | "archive" | "retry" | "replace" | null;

/** The actions, the two confirmations, and the file picker — one state machine for the header. */
function useLifecycle(one: VoiceAgentView) {
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [pending, setPending] = useState<Pending>(null);
  const [problem, setProblem] = usePortalMessage();

  const refresh = async (next: VoiceAgentView | null): Promise<void> => {
    if (next !== null) queryClient.setQueryData([...QUERY_KEY.voiceAgent, one.voiceAgentId], next);
    await queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent });
    // plan-021 review L1: a pause, a resume or an archive changes this agent's state on every pass panel that lists it
    // (and its own panel's sequence), so every pass read is read again.
    await queryClient.invalidateQueries({ queryKey: VOICE_AGENT_PASS_QUERY_KEY.all });
  };

  const run = async (what: Exclude<Pending, null>, work: () => Promise<void>): Promise<void> => {
    setPending(what);
    setProblem(null);
    try {
      await work();
    } catch (raised) {
      setProblem(() => (lifecycleRefusalOf(raised)));
    } finally {
      setPending(null);
    }
  };

  const openBuild = async (buildJobId: string): Promise<void> => {
    rememberBuildOrigin(queryClient, buildJobId, one.voiceAgentId);
    await refresh(null);
    await navigate({ to: "/building/$buildJobId", params: { buildJobId } });
  };

  return {
    pending,
    problem,
    pause: () =>
      run("pause", async () => {
        await refresh(await gateway.pauseVoiceAgent(one.voiceAgentId));
      }),
    resume: () =>
      run("resume", async () => {
        await refresh(await gateway.resumeVoiceAgent(one.voiceAgentId));
      }),
    archive: () =>
      run("archive", async () => {
        await gateway.archiveVoiceAgent(one.voiceAgentId);
        await refresh(null);
        await navigate({ to: "/agents", search: { status: "all", q: "" } });
      }),
    retry: () =>
      run("retry", async () => {
        const made = await gateway.rebuildVoiceAgent(one.voiceAgentId, "reuse");
        await openBuild(made.buildJobId);
      }),
    /** New documents: open a build that waits for them (unless one already does), upload, start. */
    replace: (files: readonly File[]) =>
      run("replace", async () => {
        if (one.latestBuild === null || !one.latestBuild.waitingForDocument) {
          await gateway.rebuildVoiceAgent(one.voiceAgentId, "replace");
        }
        for (const file of files) {
          await gateway.uploadAgentDocument(one.voiceAgentId, file, () => undefined);
        }
        const started = await gateway.startVoiceAgentBuild(one.voiceAgentId);
        await openBuild(started.buildJobId);
      }),
  };
}

export interface VoiceAgentHeadRowProps {
  readonly voiceAgent: VoiceAgentView;
  /** The kicker and the title, drawn by the Agent screen. */
  readonly heading: ReactNode;
  /** The screen's own action ("Test agent"), after the lifecycle buttons. */
  readonly extraAction: ReactNode;
  /** Opens a tab of the Agent page — "Connect a number" opens Channels. */
  readonly onOpenTab: (tab: "channels" | "test") => void;
}

/**
 * The header's title row with its buttons, and under it the panel that says what is wrong and what
 * to do. One component, so the buttons and the panel share one pending state and one problem.
 */
export function VoiceAgentHeadRow({ voiceAgent: one, heading, extraAction, onOpenTab }: VoiceAgentHeadRowProps): ReactNode {
  usePortalTranslation("operations");
  const lifecycle = useLifecycle(one);
  const [confirming, setConfirming] = useState<"pause" | "archive" | "rename" | null>(null);
  const picker = useRef<HTMLInputElement | null>(null);
  const hasLiveVersion = one.liveAgentVersionId !== null;
  const busy = lifecycle.pending !== null;
  const latest = one.latestBuild;
  const buildFailed = latest !== null && latest.state === "failed";
  const waiting = latest !== null && latest.waitingForDocument;

  // Owner decision 2026-11-02: Pause and Resume are offered only to the roles the api admits for them —
  // `rolesAdmitting(OWNER_OR_ADMIN, "voice_agent.write")`: the workspace's Owner and Admin and a preset holding
  // `voice_agent.write` (Developer). A Member, a Viewer, a Campaign manager or an organization role with no workspace
  // role would only be refused, so the buttons are not drawn for them (the wetalk app draws them the same way).
  const member = useMemberCan();
  const mayPauseOrResume = member.role !== null && member.role !== "member" && member.can("voice_agent.write");
  const canPause = mayPauseOrResume && one.state === "live" && one.pausedBy !== "operator" && hasLiveVersion;
  const canResume = mayPauseOrResume && one.state === "paused" && one.pausedBy === "member";
  // Archive is offered where the API allows it: not live, and not while a build runs.
  const buildRunning = latest !== null && (latest.state === "running" || (latest.state === "queued" && !latest.waitingForDocument));
  const canArchive = one.state !== "live" && !buildRunning && one.pausedBy !== "operator";

  const filePicker = (
    <input
      ref={picker}
      type="file"
      multiple
      accept={DOCUMENT_ACCEPT}
      className="c-lc-file"
      aria-label={portalText("operations", "choose_the_documents_to_build_from_620c3f21")}
      onChange={(event) => {
        const files = event.currentTarget.files === null ? [] : [...event.currentTarget.files];
        event.currentTarget.value = "";
        if (files.length > 0) void lifecycle.replace(files);
      }}
    />
  );
  const choose = (): void => {
    picker.current?.click();
  };

  const actions = (
    <>
      {one.archived ? null : (
        <Button variant="ghost" disabled={busy} onClick={() => setConfirming("rename")}>
          {portalText("operations", "rename_d3f4cb89")}</Button>
      )}
      {canPause ? (
        <Button variant="secondary" disabled={busy} onClick={() => setConfirming("pause")}>
          {portalText("operations", "pause_agent_896172c7")}</Button>
      ) : null}
      {canResume ? (
        <Button variant="secondary" disabled={busy} onClick={() => void lifecycle.resume()}>
          {lifecycle.pending === "resume" ? portalText("operations", "resuming_ab0bec19") : portalText("operations", "resume_b3bd0b5a")}
        </Button>
      ) : null}
      {canArchive ? (
        <Button variant="ghost" disabled={busy} onClick={() => setConfirming("archive")}>
          {portalText("operations", "remove_agent_7ba5ac8c")}</Button>
      ) : null}
    </>
  );

  let body: ReactNode = null;
  if (one.pausedBy === "operator") {
    body = (
      <>
        <p className="c-lc-title">{portalText("operations", "wetalk_paused_this_agent_90a7d616")}</p>
        <p className="c-lc-text">
          {portalText("operations", "callers_hear_that_the_line_is_not_taking_calls_at_the_mome_0ef75a28")}</p>
      </>
    );
  } else if (buildFailed) {
    const failure = latest.failure;
    const platform = failure !== null && failure.fault === "platform";
    body = (
      <>
        <p className="c-lc-title">
          {hasLiveVersion ? portalText("operations", "value_s_build_stopped_1d7fd2ff", { v0: String(latest.agentVersionLabel) }) : portalText("operations", "the_build_stopped_49821621")}
          {failure !== null && failure.stepLabel !== null ? portalText("operations", "at_value_2fdadca2", { v0: String(failure.stepLabel) }) : ""}.
        </p>
        <p className="c-lc-text">
          {failure === null || failure.message === null
            ? portalText("operations", "wetalk_did_not_record_why_this_build_stopped_try_again_or_fda71863")
            : failure.message}
        </p>
        <p className="c-lc-text">
          {hasLiveVersion
            ? portalText("operations", "value_keeps_answering_nothing_was_published_8dd26c82", { v0: String(one.liveAgentVersionLabel) })
            : portalText("operations", "nothing_was_published_and_nothing_was_charged_06bc5aea")}
        </p>
        <div className="c-lc-actions">
          <Button variant={platform || failure === null ? "primary" : "secondary"} disabled={busy} onClick={() => void lifecycle.retry()}>
            {lifecycle.pending === "retry" ? portalText("operations", "starting_e5f58095") : portalText("operations", "try_again_042c862e")}
          </Button>
          <Button variant={platform || failure === null ? "secondary" : "primary"} disabled={busy} onClick={choose}>
            {lifecycle.pending === "replace" ? portalText("operations", "uploading_d921a79a") : portalText("operations", "replace_document_0b30132f")}
          </Button>
        </div>
      </>
    );
  } else if (waiting) {
    body = (
      <>
        <p className="c-lc-title">{portalText("operations", "this_agent_s_build_is_waiting_for_its_documents_59dfb908")}</p>
        <p className="c-lc-text">{portalText("operations", "upload_the_documents_to_build_8f818978")}{latest.agentVersionLabel} {portalText("operations", "from_the_build_starts_as_soon_as_they_are_in_4bf8e24c")}</p>
        <div className="c-lc-actions">
          <Button variant="primary" disabled={busy} onClick={choose}>
            {lifecycle.pending === "replace" ? portalText("operations", "uploading_d921a79a") : portalText("operations", "upload_documents_6f8e7512")}
          </Button>
        </div>
      </>
    );
  } else if (one.state === "paused") {
    body = (
      <>
        <p className="c-lc-title">{portalText("operations", "paused_7a5f2aad")}</p>
        <p className="c-lc-text">
          {portalText("operations", "until_you_resume_it_callers_hear_sorry_this_line_isn_t_tak_93906559")}</p>
      </>
    );
  } else if (one.state === "live" && !one.reachable) {
    body = (
      <>
        <p className="c-lc-title">{portalText("operations", "next_step_connect_a_number_f9f4eb4b")}</p>
        <p className="c-lc-text">
          {one.liveAgentVersionLabel} {portalText("operations", "went_live_as_soon_as_it_was_built_but_no_number_or_endpoin_aa48997f")}</p>
        <div className="c-lc-actions">
          <Button variant="primary" onClick={() => onOpenTab("channels")}>
            {portalText("operations", "connect_a_number_f563d967")}</Button>
          <Button variant="secondary" onClick={() => onOpenTab("test")}>
            {portalText("operations", "test_it_0b448379")}</Button>
        </div>
      </>
    );
  }

  const panel =
    body === null && lifecycle.problem === null ? (
      filePicker
    ) : (
      <div className="c-lc" data-tone={buildFailed || one.pausedBy === "operator" ? "critical" : "caution"}>
        {body}
        {lifecycle.problem === null ? null : <p className="c-note c-note-critical">{lifecycle.problem}</p>}
        {filePicker}
      </div>
    );

  return (
    <>
      <div className="c-va-head-row">
        <div>{heading}<VoiceAgentAudienceTag agentTemplateCode={one.agentTemplateCode} /></div>
        <div className="c-va-actions">
          {actions}
          {extraAction}
        </div>
      </div>
      {panel}
      {/* One confirmation in the page at a time, mounted only while it is asked. */}
      {confirming === "rename" ? <RenameVoiceAgentDialog voiceAgent={one} onClose={() => setConfirming(null)} /> : null}
      {confirming === "pause" ? (
        <Dialog
          open
          title={portalText("operations", "pause_value_0aa95e25", { v0: String(one.displayName) })}
          onClose={() => setConfirming(null)}
          actions={
            <>
              <Button variant="ghost" onClick={() => setConfirming(null)}>
                {portalText("operations", "keep_it_answering_2a972f12")}</Button>
              <Button
                variant="primary"
                onClick={() => {
                  setConfirming(null);
                  void lifecycle.pause();
                }}
              >
                {portalText("operations", "pause_agent_896172c7")}</Button>
            </>
          }
        >
          <p>
            {portalText("operations", "until_you_resume_it_every_caller_hears_sorry_this_line_isn_64f0b441")}</p>
          <PassReferrersLine voiceAgentId={one.voiceAgentId} name={one.displayName} />
        </Dialog>
      ) : null}
      {confirming === "archive" ? (
        <Dialog
          open
          title={portalText("operations", "remove_value_2158a1fc", { v0: String(one.displayName) })}
          onClose={() => setConfirming(null)}
          actions={
            <>
              <Button variant="ghost" onClick={() => setConfirming(null)}>
                {portalText("operations", "keep_it_d9f36924")}</Button>
              <Button
                variant="primary"
                onClick={() => {
                  setConfirming(null);
                  void lifecycle.archive();
                }}
              >
                {portalText("operations", "remove_agent_7ba5ac8c")}</Button>
            </>
          }
        >
          <p>
            {portalText("operations", "it_leaves_your_agents_list_and_every_picker_its_calls_orde_49eba784")}</p>
          <PassReferrersLine voiceAgentId={one.voiceAgentId} name={one.displayName} />
        </Dialog>
      ) : null}
    </>
  );
}

/** The agents that pass conversations to this one, in a confirmation — nothing while unread, failed or empty. */
function PassReferrersLine({ voiceAgentId, name }: { readonly voiceAgentId: string; readonly name: string }): ReactNode {
  usePortalTranslation("voiceAgentPass");
  const referrer = useVoiceAgentPassReferrers(voiceAgentId, true);
  const text = referrer.data === undefined ? null : referrersTextOf(name, referrer.data);
  return text === null ? null : <p data-pass-referrers="">{text}</p>;
}

/**
 * The reads that show a VoiceAgent's name, read again after a rename: the agent itself and the list, conversations
 * (History, Live, the web chat), campaigns and their results, numbers, the dashboard, the audit log and the pass panels.
 */
const NAMED_IN: readonly (readonly string[])[] = [
  QUERY_KEY.voiceAgent,
  QUERY_KEY.dashboard,
  QUERY_KEY.liveSnapshot,
  QUERY_KEY.liveActivity,
  QUERY_KEY.pastConversation,
  QUERY_KEY.webChatConversation,
  QUERY_KEY.campaign,
  QUERY_KEY.surveyResult,
  QUERY_KEY.number,
  QUERY_KEY.auditEntry,
  VOICE_AGENT_PASS_QUERY_KEY.all,
];

/** "Rename": the name box, pre-filled; the API's refusal stays in the dialog (owner request 2026-10-28). */
function RenameVoiceAgentDialog({ voiceAgent: one, onClose }: { readonly voiceAgent: VoiceAgentView; readonly onClose: () => void }): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [name, setName] = useState(one.displayName);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = usePortalMessage();
  const trimmed = name.trim();

  const save = async (event?: FormEvent): Promise<void> => {
    event?.preventDefault();
    if (saving) return;
    if (trimmed === "") {
      setProblem(() => portalText("operations", "give_the_agent_a_name_d95c3597"));
      return;
    }
    if (trimmed === one.displayName) {
      onClose();
      return;
    }
    setSaving(true);
    setProblem(null);
    try {
      const renamed = await gateway.renameVoiceAgent(one.voiceAgentId, trimmed);
      queryClient.setQueryData([...QUERY_KEY.voiceAgent, one.voiceAgentId], renamed);
      onClose();
      await Promise.all(NAMED_IN.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    } catch (raised) {
      setProblem(() => apiProblemText(raised, portalText("shell", "that_did_not_work_nothing_was_changed_4b96c983")));
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      title={portalText("operations", "rename_value_5e0c41a7", { v0: String(one.displayName) })}
      onClose={onClose}
      actions={
        <>
          <Button variant="ghost" disabled={saving} onClick={onClose}>
            {portalText("operations", "cancel_77dfd213")}</Button>
          <Button variant="primary" disabled={saving || trimmed === ""} onClick={() => void save()}>
            {saving ? portalText("operations", "saving_56a2285c") : portalText("operations", "save_name_3b8f6a20")}
          </Button>
        </>
      }
    >
      <form onSubmit={(event) => void save(event)}>
        <Field
          label={portalText("operations", "agent_name_fc92420c")}
          description={portalText("operations", "the_new_name_shows_at_once_wherever_the_agent_is_named_4c7d2e19")}
          {...(problem === null ? {} : { error: problem })}
        >
          {(control) => (
            <Input
              {...control}
              name="voiceAgentName"
              autoFocus
              maxLength={VOICE_AGENT_NAME_MOST}
              value={name}
              onChange={(event) => {
                setName(event.currentTarget.value);
              }}
            />
          )}
        </Field>
      </form>
    </Dialog>
  );
}
